import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { CsIconModule } from '@components/icon';
import { ConnectionService } from '@services/connection-service';
import { UserAllocatedDoctorService } from '@services/user-allocated-doctor-service/user-allocated-doctor.service';
import { CurrentComponentService } from '@services/current-component';
import { AppConfig } from '../../app.config';

const PREGNANCY_TAG_MAP: Record<string, 'PREGNANT' | 'NEW_MOM' | 'PLANNING_FOR_PREGNANCY'> = {
  'I am pregnant': 'PREGNANT',
  'Planning pregnancy in next 6 months': 'PLANNING_FOR_PREGNANCY',
  'I am a new mom & breastfeeding': 'NEW_MOM',
};

@Component({
  selector: 'app-safety-check-question',
  templateUrl: './safety-check-question.component.html',
  imports: [CsIconModule, NgTemplateOutlet],
  host: { class: 'tw-block tw-h-full' },
})
export class SafetyCheckQuestionComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly conn = inject(ConnectionService);
  private readonly appConfig = inject(AppConfig);
  private readonly allocatedDoctorService = inject(UserAllocatedDoctorService);
  private readonly currentComponentService = inject(CurrentComponentService);

  userName: string = '';
  doctorImage: string = '/assets/images/dr-charu.png';
  gender: 'male' | 'female' = 'female';
  isCodOrder: boolean = false;
  currentView: 'question' | 'consent' = 'question';
  showPopup: boolean = false;
  showWhyRemoved: boolean = false;
  showNotSurePopup: boolean = false;
  showCallScheduledPopup: boolean = false;
  callAlreadyRequested: boolean = false;
  pregnancyAlreadyAnswered: boolean = false;
  loading: boolean = false;

  constructor() {
    this.currentComponentService.set(this);
    const user = this.conn.getActingUser();
    const params = this.route.snapshot.queryParams;
    this.userName = user?.get('PatientName')?.split(' ')?.[0] || '';
    if (this.userName) {
      this.userName = this.userName.charAt(0).toUpperCase() + this.userName.slice(1).toLowerCase();
    }
    this.gender = user?.get('Gender') === this.appConfig.Shared.Gender.FEMALE ? 'female' : 'male';
    this.isCodOrder = params['paymentType'] === 'COD';

    if (this.gender === 'male' || params['view'] === 'consent') {
      this.currentView = 'consent';
      this.pregnancyAlreadyAnswered = true;
    }
  }

  async ngOnInit(): Promise<void> {
    this.loadDoctorImage();
    this.conn.getAllPendingCallsForType('IsotretinoinConsentCall').then((calls: any): void => {
      if (Array.isArray(calls) && calls.length) this.callAlreadyRequested = true;
    }).catch(() => {});
  }

  private async loadDoctorImage(): Promise<void> {
    const doctor = await this.allocatedDoctorService.getAllocatedDoctor(true).catch(() => null);
    if (doctor?.doctorDisplayImage) {
      this.doctorImage = doctor.doctorDisplayImage;
    }
  }

  private get orderId(): string {
    return this.route.snapshot.queryParams['orderId'] || '';
  }

  goBack(): void {
    if (this.currentView === 'consent' && this.gender === 'female' && !this.pregnancyAlreadyAnswered) {
      this.currentView = 'question';
      return;
    }
    this.goToHome();
  }

  async handleBackPress(): Promise<boolean> {
    this.goBack();
    return true;
  }

  ngOnDestroy(): void {
    this.currentComponentService.remove(this);
  }

  async selectOption(option: string): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    const pregnancyTag = PREGNANCY_TAG_MAP[option];
    try {
      await this.conn.updatePregnancyStatus(pregnancyTag);
      if (pregnancyTag) {
        await this.conn.processIsotretinoinConsent(this.orderId, false);
      }
    } catch (e) {
      console.error('Failed to process option:', e);
      this.loading = false;
      return;
    }
    this.pregnancyAlreadyAnswered = true;
    this.loading = false;
    if (pregnancyTag) {
      this.showPopup = true;
    } else {
      this.currentView = 'consent';
    }
  }

  closePopup(): void {
    this.showPopup = false;
    this.showWhyRemoved = false;
    this.goToHome();
  }

  toggleWhyRemoved(): void {
    this.showWhyRemoved = true;
  }

  onNotSureClick(): void {
    if (this.callAlreadyRequested) {
      this.showCallScheduledPopup = true;
    } else {
      this.showNotSurePopup = true;
    }
  }

  closeNotSurePopup(): void {
    this.showNotSurePopup = false;
  }

  async requestCall(): Promise<void> {
    if (this.loading) return;
    this.showNotSurePopup = false;
    this.loading = true;
    try {
      await this.conn.createIsotretinoinConsentCall(this.orderId);
      this.callAlreadyRequested = true;
      this.loading = false;
      this.showCallScheduledPopup = true;
    } catch (e) {
      console.error('Failed to create isotretinoin consent call:', e);
      this.loading = false;
      this.showNotSurePopup = true;
    }
  }

  async acceptAndConfirm(): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    try {
      await this.conn.processIsotretinoinConsent(this.orderId, true);
    } catch (e) {
      console.error('Failed to process isotretinoin consent:', e);
      this.loading = false;
      return;
    }
    this.loading = false;
    this.router.navigate(['/order/success'], {
      queryParams: { ...this.route.snapshot.queryParams, source: 'isotretinoin-accepted' },
    });
  }

  viewUpdatedTreatment(): void {
    this.conn.navigateToURL('/regimen/instructions?class=FACE&back=home', false, true);
  }

  async removeIsotretinoin(): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    try {
      await this.conn.processIsotretinoinConsent(this.orderId, false);
    } catch (e) {
      console.error('Failed to process isotretinoin removal:', e);
      this.loading = false;
      return;
    }
    this.loading = false;
    this.router.navigate(['/order/success'], {
      queryParams: { source: 'isotretinoin-removal', orderId: this.orderId },
    });
  }

  goToHome(): void {
    this.router.navigate(['/user'], { queryParams: { tab: 'home' } });
  }
}
